import { useSelector } from 'react-redux';
import { useFetch } from '@stashinvest/use-fetch';
import useOnMount from 'stash/hooks/useOnMount';

import { selectIsAuthenticated, selectProduct, selectUUID } from 'stash/selectors';
import { getCookie } from 'stash/utils/cookies';
import {
	AUTH_CREATED_ON_WEB_COOKIE_NAME,
	AUTH_SESSION_TYPE_COOKIE_NAME,
} from 'stash/constants/cookies';
import {
	getBookieAttributionData,
	getInstrumentationProperties,
	setStoredInstrumentationAttributions,
} from 'stash/utils/attribution';
import { datadogRum } from '@datadog/browser-rum';

const useAttributionOnUserCreate = () => {
	const isAuthenticated = useSelector(selectIsAuthenticated);
	const userUuid = useSelector(selectUUID);
	const product = useSelector(selectProduct);
	const authCreatedOnWebCookie = getCookie(AUTH_CREATED_ON_WEB_COOKIE_NAME);
	const authSessionType = getCookie(AUTH_SESSION_TYPE_COOKIE_NAME);

	const { fetch: postPaulRevereAttribution } = useFetch({
		method: useFetch.POST,
		path: '/apis/attributions/api/v1/users/:uuid/attributions',
	});

	const { fetch: instrumentationAddWebProperties } = useFetch({
		method: useFetch.POST,
		path: '/apis/customers/v1/analytics/web_properties',
	});

	useOnMount(() => {
		// Only fire attribution on initial user create
		if (isAuthenticated && (authCreatedOnWebCookie || authSessionType === 'signup')) {
			const web_reg_version = `${window.Stash.version}+${product || 'universal'}`;

			datadogRum.addAction('profile_create');
			const utm_source = getCookie('utm_source');
			const utm_medium = getCookie('utm_medium');
			const utm_campaign = getCookie('utm_campaign');
			const utm_content = getCookie('utm_content');
			const utm_provider = getCookie('utm_provider');
			const stash_code = getCookie('stashPromoCodeV2') || getCookie('stashPromoCode');

			const attribution_data = getBookieAttributionData({
				utm_provider,
				utm_source,
				utm_medium,
				utm_campaign,
				utm_content,
			});

			const user = {
				utm_data: {
					utm_source,
					utm_medium,
					utm_campaign,
					utm_content,
				},
				google_client_id: getCookie('__cid'),
				stash_code,
				web_reg_version,
				...attribution_data,
			};

			const instrumentationProperties = getInstrumentationProperties();

			if (instrumentationProperties) {
				const instrumentationWebPropertiesBody = {
					userId: userUuid,
					properties: instrumentationProperties,
				};

				instrumentationAddWebProperties({ body: instrumentationWebPropertiesBody }).catch(
					() => {
						instrumentationAddWebProperties({ body: instrumentationWebPropertiesBody });
					}
				);
				setStoredInstrumentationAttributions(instrumentationProperties);
			}

			postPaulRevereAttribution({ body: user }).catch((err) => {
				// we're seeing some flaky connection issues, so retry once if the call fails
				console.error(err);
				postPaulRevereAttribution({ body: user });
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	});
};

export default useAttributionOnUserCreate;
